import {inject, Injectable} from '@angular/core';
import {BehaviorSubject, Observable, tap,} from 'rxjs';
import {UrlEnum} from "app/core/types/url-enum";
import {HttpClient, HttpHeaders} from "@angular/common/http";
import {UrlService} from "app/core/services/url.service";
import {
    InstrumentAddTOCycleDTO,
    MedicalInstrument,
    SterilizationCycleDTO,
    SterilizationCyclePagableDTO,
    SterilizationCycleWithInstruments, SterilizationDeviceJournalEntry, SterilizationDeviceJournalEntryToCreate,
    SterilizationDeviceState,
    SterilizedInstrument,
    SterilizerDeviceDTO,
    StockRating
} from "./sterilization.types";
import {ObservableErrHandler} from "../../core/types/err.handling.types";
import {ErrorHandlingService} from "../../core/services/error.handling.service";
import {UpdateEntities} from "../patient/interventions/intervention.types";
import {DateTimeService} from "../../core/services/date.time.service";

@Injectable({ providedIn: 'root' })
export class SterilizationService {
    //services
    private readonly _httpClient = inject(HttpClient);
    private readonly _urlService : UrlService = inject(UrlService);
    private readonly _dtsrv : DateTimeService = inject(DateTimeService);
    private _capturedText : string = "";


    private _errHandlService: ErrorHandlingService =inject(ErrorHandlingService);
    private _globalKeyboardListenerAttached = false;

    //observables
    private readonly _instruments: BehaviorSubject<MedicalInstrument[] | null> = new BehaviorSubject([]);
    private readonly _sterilizationDevices: BehaviorSubject<SterilizerDeviceDTO[] | null> = new BehaviorSubject([]);
    private readonly _sterilizedInstruments: BehaviorSubject<SterilizedInstrument[] | null> = new BehaviorSubject([]);
    private readonly _batches: BehaviorSubject<SterilizationCyclePagableDTO | null> = new BehaviorSubject(null);
    private readonly _stockData: BehaviorSubject<Map<string, number>> = new BehaviorSubject(new Map<string, number>());
    private readonly _lowStockDetected: BehaviorSubject<boolean> = new BehaviorSubject(false);
    private readonly _busy: BehaviorSubject<boolean> = new BehaviorSubject(false);
    private pageSize= 7;


    get capturedText(){
        return this._capturedText;
    }

    set capturedText(txt:string){
        this._capturedText = txt;
    }

    get busy$(){
        return this._busy.asObservable();
    }

    get instruments$(){
        return this._instruments.asObservable();
    }

    get devices$(){
        return this._sterilizationDevices.asObservable();
    }

    get batches$(){
        return this._batches.asObservable();
    }

    get sterilizedInstruments$(){
        return this._sterilizedInstruments.asObservable();
    }

    get lowStockDetected$(){
        return this._lowStockDetected.asObservable();
    }

    get stockData$(){
        return this._stockData.asObservable();
    }

    get globalKeyboardListenerAttached(): boolean {
        return this._globalKeyboardListenerAttached;
    }

    set globalKeyboardListenerAttached(value: boolean) {
        this._globalKeyboardListenerAttached = value;
    }

    public addInstrument(instrument:MedicalInstrument){
        this._busy.next(true);

        const headers = new HttpHeaders().set('Content-Type', 'application/json; charset=utf-8');

        const obs = this._httpClient.post<string>(
            this._urlService.getUrl(UrlEnum.private_medical_instrument), JSON.stringify(instrument), {headers: headers}
        );

        const errWrapper: ObservableErrHandler = {
            observable: obs,
            err_title: "Error"
        };

        this._errHandlService.watchThis(errWrapper);
        errWrapper.observable = errWrapper.observable.pipe(tap({
                next: id => {
                    instrument.id = id;
                    this._instruments.value.push(instrument);
                    this._instruments.next(this._instruments.value);
                    this.buildUpStockData();
                },
                error: err => {
                    this._busy.next(false);
                },
                complete: () => {
                    this._busy.next(false);
                }
            }
        ));
        errWrapper.observable.subscribe();
    }

    public getInstrumentsFromBE(){
        this._busy.next(true);

        const obs = this._httpClient.get<MedicalInstrument[]>(
            this._urlService.getUrl(UrlEnum.private_medical_instrument_all)
        );

        const errWrapper: ObservableErrHandler = {
            observable: obs,
            err_title: "Error"
        };

        this._errHandlService.watchThis(errWrapper);
        errWrapper.observable = errWrapper.observable.pipe(tap({
                next: instruments => {
                    const filtered: MedicalInstrument[] = instruments.filter(instr => !instr.retired);
                    this._instruments.next(filtered);
                    this.buildUpStockData();
                },
                error: err => {
                    this._busy.next(false);
                },
                complete: () => {
                    this._busy.next(false);
                }
            }
        ));
        errWrapper.observable.subscribe();
    }


    addSterilizerDevice(device: SterilizerDeviceDTO) {
        this._busy.next(true);
        if(!device.state){
            device.state = SterilizationDeviceState.COMPLIANT;
        }

        const headers = new HttpHeaders().set('Content-Type', 'application/json; charset=utf-8');

        const obs = this._httpClient.post<string>(
            this._urlService.getUrl(UrlEnum.private_sterilization_device), JSON.stringify(device), {headers: headers}
        );

        const errWrapper: ObservableErrHandler = {
            observable: obs,
            err_title: "Error"
        };

        this._errHandlService.watchThis(errWrapper);
        errWrapper.observable = errWrapper.observable.pipe(tap({
                next: id => {
                    device.id = id;
                    this._sterilizationDevices.value.push(device);
                    this._sterilizationDevices.next(this._sterilizationDevices.value);
                },
                error: err => {
                    this._busy.next(false);
                },
                complete: () => {
                    this._busy.next(false);
                }
            }
        ));
        errWrapper.observable.subscribe();
    }

    public getSterilizationDevicesFromBE(){
        this._busy.next(true);

        const obs = this._httpClient.get<SterilizerDeviceDTO[]>(
            this._urlService.getUrl(UrlEnum.private_sterilization_device_all)
        );

        const errWrapper: ObservableErrHandler = {
            observable: obs,
            err_title: "Error"
        };

        this._errHandlService.watchThis(errWrapper);
        errWrapper.observable = errWrapper.observable.pipe(tap({
                next: devices => {
                    const filtered: SterilizerDeviceDTO[] = devices.filter(dev => !dev.retired);
                    this._sterilizationDevices.next(filtered);
                },
                error: err => {
                    this._busy.next(false);
                },
                complete: () => {
                    this._busy.next(false);
                }
            }
        ));
        errWrapper.observable.subscribe();
    }

    updateDevice(device:SterilizerDeviceDTO, remove:boolean) {
        device.retired = remove;
        const headers = new HttpHeaders().set('Content-Type', 'application/json; charset=utf-8');
        this._busy.next(true);
        const obs:Observable<any> = this._httpClient.put<any>(
            this._urlService.getUrl(UrlEnum.private_sterilization_device_update)
                .replace("{id}", device.id),
            JSON.stringify(device),
            {headers: headers});

        const errWrapper : ObservableErrHandler = {
            observable: obs,
            err_title:"Error"
        };
        this._errHandlService.watchThis(errWrapper);
        errWrapper.observable.subscribe({
            next: value => {
                const index = this._sterilizationDevices.value.findIndex(int => int.id === device.id);
                if(remove){
                    this._sterilizationDevices.value.splice(index, 1);
                }else{
                    this._sterilizationDevices.value[index] = device;
                }
                this._sterilizationDevices.next(this._sterilizationDevices.value);
            },
            error: err => {
                this._busy.next(false);
            },
            complete: () => {
                this._busy.next(false);
            }
        })
    }

    updateInstrument(instrument:MedicalInstrument, remove:boolean) {
        instrument.retired = remove;
        const headers = new HttpHeaders().set('Content-Type', 'application/json; charset=utf-8');
        this._busy.next(true);
        const obs:Observable<any> = this._httpClient.put<any>(
            this._urlService.getUrl(UrlEnum.private_medical_instrument_update)
                .replace("{id}", instrument.id),
            JSON.stringify(instrument),
            {headers: headers});

        const errWrapper : ObservableErrHandler = {
            observable: obs,
            err_title:"Error"
        };
        this._errHandlService.watchThis(errWrapper);
        errWrapper.observable.subscribe({
            next: value => {
                const index = this._instruments.value.findIndex(int => int.id === instrument.id);
                if(remove){
                    this._instruments.value.splice(index, 1);
                }else{
                    this._instruments.value[index] = instrument;
                }
                this._instruments.next(this._instruments.value);
                this.buildUpStockData();
            },
            error: err => {
                this._busy.next(false);
            },
            complete: () => {
                this._busy.next(false);
            }
        })
    }

    createBatch(batch: SterilizationCycleDTO): Observable<any> {
        this._busy.next(true);

        const headers = new HttpHeaders().set('Content-Type', 'application/json; charset=utf-8');

        const obs = this._httpClient.post<string>(
            this._urlService.getUrl(UrlEnum.private_sterilization_cycle), JSON.stringify(batch), {headers: headers}
        );

        const errWrapper: ObservableErrHandler = {
            observable: obs,
            err_title: "Error"
        };

        this._errHandlService.watchThis(errWrapper);
        errWrapper.observable = errWrapper.observable.pipe(tap({
                next: id => {
                    batch.id = id;
                    //initialize batch page refresh
                    this.getSterilizationCyclePageFromBE(0);
                },
                error: err => {
                    this._busy.next(false);
                },
                complete: () => {
                    this._busy.next(false);
                }
            }
        ));
        return errWrapper.observable;
    }

    addInstrumentsToBatch(instruments: InstrumentAddTOCycleDTO[], batchID:string) {
        this._busy.next(true);

        const headers = new HttpHeaders().set('Content-Type', 'application/json; charset=utf-8');

        const obs = this._httpClient.post<string>(
            this._urlService.getUrl(
                UrlEnum.private_sterilization_cycle_add_instrument_bulk)
                    .replace('{id}', batchID)
            , JSON.stringify(instruments), {headers: headers}
        );

        const errWrapper: ObservableErrHandler = {
            observable: obs,
            err_title: "Error"
        };

        this._errHandlService.watchThis(errWrapper);
        errWrapper.observable = errWrapper.observable.pipe(tap({
                next: id => {

                },
                error: err => {
                    this._busy.next(false);
                },
                complete: () => {
                    this._busy.next(false);
                }
            }
        ));
        return errWrapper.observable;
    }

    public getSterilizationCyclePageFromBE(page:number){
        this._busy.next(true);
        const obs = this._httpClient.get<SterilizationCyclePagableDTO>(this._urlService.getUrl(UrlEnum.private_sterilization_cycle_all)
            .replace("{page}", page+"")
            .replace("{size}", this.pageSize+"")
            .replace("{sortBy}", "timeOfSterilization")
        );

        const errWrapper: ObservableErrHandler = {
            observable: obs,
            err_title: "Error"
        };
        this._errHandlService.watchThis(errWrapper);
        errWrapper.observable = errWrapper.observable.pipe(tap({
                next: sterilizationCyclePage => {
                    this._batches.next(sterilizationCyclePage);
                },
                error: err => {
                    this._busy.next(false);
                },
                complete: () => {
                    this._busy.next(false);
                }
            }
        ));
        errWrapper.observable.subscribe();
    }

    public getSterilizationCycle(id:string):Observable<SterilizationCycleWithInstruments>{
        this._busy.next(true);
        const obs = this._httpClient.get<SterilizationCycleWithInstruments>(this._urlService.getUrl(UrlEnum.private_sterilization_cycle_get)
            .replace("{id}", id)
        );

        const errWrapper: ObservableErrHandler = {
            observable: obs,
            err_title: "Error"
        };
        this._errHandlService.watchThis(errWrapper);
        errWrapper.observable = errWrapper.observable.pipe(tap({
                next: sterilizationCyclePage => {

                },
                error: err => {
                    this._busy.next(false);
                },
                complete: () => {
                    this._busy.next(false);
                }
            }
        ));
        return errWrapper.observable;
    }

    public getAvailableSterilizedInstruments():Observable<SterilizedInstrument[]>{
        this._busy.next(true);
        const obs = this._httpClient.get<SterilizedInstrument[]>(this._urlService.getUrl(UrlEnum.private_sterilization_instruments_by_status)
            .replace("{used}", "false")
        );

        const errWrapper: ObservableErrHandler = {
            observable: obs,
            err_title: "Error"
        };
        this._errHandlService.watchThis(errWrapper);
        errWrapper.observable = errWrapper.observable.pipe(tap({
                next: instruments => {
                    this._sterilizedInstruments.next(instruments.filter(instr => instr.compliant === true));
                    this.buildUpStockData();
                },
                error: err => {
                    this._busy.next(false);
                },
                complete: () => {
                    this._busy.next(false);
                }
            }
        ));
        return errWrapper.observable;
    }

    getEventLinkedInstruments(evtID:string):Observable<SterilizedInstrument[]>{
        this._busy.next(true);
        const obs = this._httpClient.get<SterilizedInstrument[]>(this._urlService.getUrl(UrlEnum.private_event_linked_sterilization_instruments)
            .replace("{eventId}", evtID)
        );

        const errWrapper: ObservableErrHandler = {
            observable: obs,
            err_title: "Error"
        };
        this._errHandlService.watchThis(errWrapper);
        errWrapper.observable = errWrapper.observable.pipe(tap({
                next: sterilizationCyclePage => {

                },
                error: err => {
                    this._busy.next(false);
                },
                complete: () => {
                    this._busy.next(false);
                }
            }
        ));
        return errWrapper.observable;
    }

    updateLinkSterilizedInstrumentToEvent(instruments:SterilizedInstrument[], eventID:string, link:boolean, onSuccess: () => void ) {
        const headers = new HttpHeaders().set('Content-Type', 'application/json; charset=utf-8');
        const entities: UpdateEntities = new UpdateEntities(instruments.map(instrument => instrument.id));
        this._busy.next(true);
        let obs: Observable<any> = null;
        if(link){
            obs = this._httpClient.put<any>(
                this._urlService.getUrl(UrlEnum.private_event_link_sterilization_instruments_bulk)
                    .replace("{eventId}", eventID),
                JSON.stringify(entities),
                {headers: headers});
        }else{
           obs = this._httpClient.delete<any>(
                this._urlService.getUrl(UrlEnum.private_event_link_sterilization_instruments_bulk)
                    .replace("{eventId}", eventID),
                {headers: headers, body: JSON.stringify(entities)});
        }

        const errWrapper: ObservableErrHandler = {
            observable: obs,
            err_title: "Error",
            success_title: "Done",
            success_msg: "Successfully "+(link?"linked":"unlinked")+" labels!"
        };
        this._errHandlService.watchThis(errWrapper);
        errWrapper.observable
            .pipe(tap(()=>{
                onSuccess();
            }))
            .subscribe({
            next: value => {

                instruments.forEach(instr => {
                    if(link) {
                        const index = this._sterilizedInstruments.value.findIndex(int => int.id === instr.id);
                        if (index > -1) {
                            this._sterilizedInstruments.value.splice(index, 1);
                        }
                    }else{
                        this._sterilizedInstruments.value.push(instr);
                    }
                })
                this._sterilizedInstruments.next(this._sterilizedInstruments.value);
                this.buildUpStockData();
            },
            error: err => {
                this._busy.next(false);
            },
            complete: () => {
                this._busy.next(false);
            }
        });
        return errWrapper.observable;
    }

    updateSterilizedInstrumentCompliant(instrumentID:string, compliant:boolean, onSuccess: () => void ) {
        const headers = new HttpHeaders().set('Content-Type', 'application/json; charset=utf-8');
        this._busy.next(true);
        let obs: Observable<any> = this._httpClient.put<any>(
            this._urlService.getUrl(UrlEnum.private_sterilization_instruments_update_compromised)
                .replace("{id}", instrumentID),
            JSON.stringify(compliant),
            {headers: headers});


        const errWrapper: ObservableErrHandler = {
            observable: obs,
            err_title: "Error",
            success_title: "Done",
            success_msg: "Successfully marked label as "+(compliant?"compliant":"not compliant")+"!"
        };
        this._errHandlService.watchThis(errWrapper);
        errWrapper.observable
            .pipe(tap(()=>{
                onSuccess();
            }))
            .subscribe({
                next: value => {
                    const index = this._sterilizedInstruments.value.findIndex(int => int.id === instrumentID);
                    if (index > -1 && !compliant) {
                        this._sterilizedInstruments.value.splice(index, 1);
                        this._sterilizedInstruments.next(this._sterilizedInstruments.value);
                        this.buildUpStockData();
                    }
                },
                error: err => {
                    this._busy.next(false);
                },
                complete: () => {
                    this._busy.next(false);
                }
            });
        return errWrapper.observable;
    }

    public getSterilizationDeviceLog(deviceID:string): Observable<SterilizationDeviceJournalEntry[]> {
        this._busy.next(true);

        const obs = this._httpClient.get<SterilizationDeviceJournalEntry[]>(
            this._urlService.getUrl(UrlEnum.private_get_sterilization_device_journal)
                .replace("{sterilizationDevicesId}",deviceID)
        );

        const errWrapper: ObservableErrHandler = {
            observable: obs,
            err_title: "Error"
        };

        this._errHandlService.watchThis(errWrapper);
        errWrapper.observable = errWrapper.observable.pipe(tap({
                next: devices => {
                },
                error: err => {
                    this._busy.next(false);
                },
                complete: () => {
                    this._busy.next(false);
                }
            }
        ));
        return errWrapper.observable;
    }

    public addToSterilizationDeviceLog(deviceID:string, newState: SterilizationDeviceState, observation:string): Observable<SterilizationDeviceJournalEntry> {
        this._busy.next(true);

        const payload: SterilizationDeviceJournalEntryToCreate ={
            sterilizationDeviceId: deviceID,
            newState: newState,
            logContent: observation
        }

        const headers = new HttpHeaders().set('Content-Type', 'application/json; charset=utf-8');

        const obs = this._httpClient.post<SterilizationDeviceJournalEntry>(
            this._urlService.getUrl(UrlEnum.private_sterilization_device_journal), JSON.stringify(payload), {headers: headers}
        );

        const errWrapper: ObservableErrHandler = {
            observable: obs,
            err_title: "Error"
        };

        this._errHandlService.watchThis(errWrapper);
        errWrapper.observable = errWrapper.observable.pipe(tap({
                next: logEntry => {
                    const device: SterilizerDeviceDTO = this._sterilizationDevices.value.find(device => device.id === deviceID);
                    if(device){
                        device.state = newState;
                        this._sterilizationDevices.next(this._sterilizationDevices.value);
                    }
                },
                error: err => {
                    this._busy.next(false);
                },
                complete: () => {
                    this._busy.next(false);
                }
            }
        ));
        return errWrapper.observable;
    }



    private buildUpStockData(){
        this._stockData.value.clear();

        //initialize all missing instruments
        this._instruments.value.forEach(instrument => {
            if(!this._stockData.value.get(instrument.name)) {
                this._stockData.value.set(instrument.name, 0);
            }
        });

        this._sterilizedInstruments.value.forEach(bag => {
            if(!this._stockData.value.get(bag.medicalInstrumentName)) {
                this._stockData.value.set(bag.medicalInstrumentName, 1);
            }else{
                const currentValue = this._stockData.value.get(bag.medicalInstrumentName);
                this._stockData.value.set(bag.medicalInstrumentName, currentValue+1);
            }
        });

        this._stockData.next(this._stockData.value);

        var lowStock: boolean = false;
        this._stockData.value.forEach((_value, key) => {
            if(this.getStockRating(_value) === StockRating.LOW){
                lowStock = true;
            }
        })

        this._lowStockDetected.next(lowStock);
    }

    public getStockRating(nr:number): StockRating
    {
        if(nr < 5 ){
            return StockRating.LOW;
        }
        if(nr >= 5 && nr <= 10 ){
            return StockRating.GOOD;
        }
        return  StockRating.HI;
    }

    getInstrumentsIfNotAllready() {
        if(this._instruments.value?.length == 0){
            this.getInstrumentsFromBE();
        }
    }

    getSterilizedInstrumentsIfNotAllready() {
        if(this._sterilizedInstruments.value?.length == 0){
            this.getAvailableSterilizedInstruments().subscribe();
        }
    }


    getDeviceInfo(bag:SterilizedInstrument){
        let name = bag.sterilizationDeviceName;
        if(name.length > 13 ){
            name=name.substring(0,10)+"..."
        }
        return name +"/"+bag.sterilizationDeviceSeries;
    }

    private clipText(str:string, nrChars:number):string{
        return str.length > nrChars ?
            str.substring(0, nrChars-1) : str;
    }

    printLabels(labels: SterilizedInstrument[]) {
        // Get the div content
        let labelHtmlData = ""
        labels.forEach(label => {

            // clip long text
            const instrName = this.clipText(label.medicalInstrumentName, 30);
            const operatorName =  this.clipText(label.createdBy, 20);

            const labelData = `
                <div class="label">
                    <div class="header">${instrName}</div>
                    <div class="split-parent">
                        <div class="section column">
                            <span>${operatorName}</span>
                        </div>
                        <div class="section column">
                            <span>
                                ${ label.sterilizationDeviceCycleCount}/
                                ${ label.sterilizationDeviceCycleCountInDay}/
                                ${ label.bagCount}
                            </span>
                        </div>
                    </div>

                    <div class="split-parent">
                        <div class="section column">
                            <span>${this._dtsrv.getDateAndTimeOfSterilization(label)}</span>
                        </div>

                        <div class="section column highlight">
                            <span>${this._dtsrv.getExpirationDateAndTime(label)}</span>
                        </div>
                    </div>

                    <div class="section">
                        <span>${this.getDeviceInfo(label)}</span>
                    </div>
                    <div class="id">${label.friendlyId}</div>
                </div>
            `
            labelHtmlData+="\n"+labelData;
        })

        const css=`
                <style>
                    @page {
                        size: 62mm 16mm; /* Adjust the size of the page if needed */
                        margin: 0;
                    }

                    body {
                        margin: 0;
                        font-family: Arial, sans-serif;
                        -webkit-print-color-adjust: exact;
                    }

                    .label {
                        width: 62mm;
                        height: 16mm;
                        outline: 1px solid black;
                        outline-offset: -2mm;
                        border-radius: 10px;
                        padding: 3mm;
                        page-break-after: always;
                        margin: 0;
                    }

                    .header {
                        text-align: center;
                        font-size: 14px;
                        font-weight: bold;
                        border-bottom: 1px solid black;
                        margin-top: -1mm;
                    }

                    .split-parent{
                        display: flex; /* Enables a 2-column setup */
                        justify-content: flex-start; /* Aligns columns to the left */
                        align-items: flex-start;
                        gap: 3mm; /* Space between the columns */
                        border-bottom: 1px solid black;
                    }

                    .column{
                        display: inline-block; /* Ensures the width matches content */
                    }

                    .section {

                    }

                    .section span {
                        display: block;
                        font-size: 10px;
                    }

                    .section span.hint {
                        font-size: 9px;
                        color: red;
                        font-style: italic;
                    }

                    .highlight {
                        color: red;
                        font-weight: bold;
                    }

                    .id {
                        text-align: center;
                        background-color: black;
                        color: white;
                        padding: 0.2mm;
                        font-weight: bold;
                        font-size: 12px;
                        margin: 0 -1.2mm 0 -1.2mm;
                        border-radius: 0 0 2px 2px;
                    }
                </style>
        `;

        this.printLabelsWithHtmlLabelData(labelHtmlData, css);
    }


    public printLabelsWithHtmlLabelData(labelHtmlData: string, css:string) {
        // Get the div content

        // Open a new window
        const newWindow = window.open();
        if (!newWindow) {
            console.error('Failed to open new window!');
            return;
        }

        const docHtml = `
            <!DOCTYPE html>
            <html lang="en">
            <head>
                <meta charset="UTF-8">
                <meta name="viewport" content="width=device-width, initial-scale=1.0">
                <title>Print sterilization labels</title>
                ${css}
            </head>
            <body>
                ${labelHtmlData}
            </body>

            </html>
        `;

        // Build HTML for the new window
        newWindow.document.write(docHtml);

        // Ensure the document is fully loaded
        newWindow.document.close();

        setTimeout(() => {
            // Print the new window
            newWindow.print();

            newWindow.close();
        }, 1000);

    }


}
